import { useRef, useEffect } from 'react';
import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { Lensflare, LensflareElement } from 'three/examples/jsm/objects/Lensflare.js';

// NEXU COSMOS — texturas locais em alta resolução. Terra, Sol (real texturizado brilhante),
// Lua visível, satélites minúsculos. Câmera orbitando: dia e noite.
const T = '/cosmos/';

export default function NexuThreeCore({ phase = 'idle' }) {
  const mountRef = useRef(null);
  const phaseRef = useRef(phase);
  useEffect(() => { phaseRef.current = phase; }, [phase]);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    const W = () => mount.clientWidth || window.innerWidth;
    const H = () => mount.clientHeight || window.innerHeight;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(46, W() / H(), 0.01, 100000);
    camera.position.set(0, 1.0, 7);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, premultipliedAlpha: false });
    renderer.setClearColor(0x000000, 0);
    renderer.setSize(W(), H());
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.05;
    renderer.domElement.style.cssText = 'width:100%;height:100%;display:block';
    mount.appendChild(renderer.domElement);

    const loader = new THREE.TextureLoader();
    const maxAniso = renderer.capabilities.getMaxAnisotropy();
    const load = (u, srgb = true) => { const t = loader.load(T + u); t.anisotropy = maxAniso; if (srgb) t.colorSpace = THREE.SRGBColorSpace; return t; };

    const EARTH_R = 2;
    const sunPos = new THREE.Vector3(120, 20, 35); // mais longe

    // ---------- SOL: esfera de plasma (menor, longe) + glow que DISSIPA (sprites radiais) ----------
    const sunMat = new THREE.MeshBasicMaterial({ map: load('sun.jpg'), color: 0xffe9b0 });
    const sun = new THREE.Mesh(new THREE.SphereGeometry(7, 64, 64), sunMat);
    sun.position.copy(sunPos); scene.add(sun);
    // glow radial suave (textura de gradiente que dissipa nas bordas — não círculo sólido)
    const glowCanvas = document.createElement('canvas'); glowCanvas.width = glowCanvas.height = 256;
    const gx = glowCanvas.getContext('2d');
    const gg = gx.createRadialGradient(128,128,0,128,128,128);
    gg.addColorStop(0,'rgba(255,245,210,0.9)'); gg.addColorStop(0.25,'rgba(255,200,110,0.5)');
    gg.addColorStop(0.55,'rgba(255,150,60,0.18)'); gg.addColorStop(1,'rgba(255,120,40,0)');
    gx.fillStyle=gg; gx.fillRect(0,0,256,256);
    const glowTex = new THREE.CanvasTexture(glowCanvas);
    const glowSprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: glowTex, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, opacity: 0.9 }));
    glowSprite.scale.set(46, 46, 1); glowSprite.position.copy(sunPos); scene.add(glowSprite);
    const sunLight = new THREE.DirectionalLight(0xfff4e6, 3.4); sunLight.position.copy(sunPos); scene.add(sunLight);
    scene.add(new THREE.AmbientLight(0x0a1220, 0.08));

    // ---------- TERRA ----------
    const earthGroup = new THREE.Group(); earthGroup.rotation.z = 0.409; scene.add(earthGroup);
    const earthMat = new THREE.MeshPhongMaterial({
      map: load('earth_day.jpg'),
      bumpMap: load('earth_bump.jpg', false), bumpScale: 0.05,
      specularMap: load('earth_water.png', false), specular: new THREE.Color(0x6a8fbf), shininess: 22,
      emissiveMap: load('earth_lights.png'), emissive: new THREE.Color(0xffd9a0), emissiveIntensity: 1.6,
    });
    const earth = new THREE.Mesh(new THREE.SphereGeometry(EARTH_R, 160, 160), earthMat);
    earthGroup.add(earth);
    const clouds = new THREE.Mesh(new THREE.SphereGeometry(EARTH_R * 1.01, 128, 128),
      new THREE.MeshPhongMaterial({ map: load('earth_clouds.png'), transparent: true, opacity: 0.9, depthWrite: false }));
    earthGroup.add(clouds);
    // atmosfera realista (glow fino no lado do dia)
    const atmoMat = new THREE.ShaderMaterial({
      transparent: true, side: THREE.BackSide, blending: THREE.AdditiveBlending, depthWrite: false,
      uniforms: { glow: { value: new THREE.Color(0x5aa9ff) }, lightDir: { value: sunPos.clone().normalize() } },
      vertexShader: 'varying vec3 vWN; varying vec3 vVN; void main(){ vWN=normalize(mat3(modelMatrix)*normal); vVN=normalize(normalMatrix*normal); gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0);}',
      fragmentShader: 'uniform vec3 glow; uniform vec3 lightDir; varying vec3 vWN; varying vec3 vVN; void main(){ float rim=pow(1.0-abs(vVN.z),3.5); float lit=clamp(dot(vWN,lightDir)+0.2,0.0,1.0); gl_FragColor=vec4(glow, rim*lit*0.6);}'
    });
    const atmo = new THREE.Mesh(new THREE.SphereGeometry(EARTH_R * 1.02, 96, 96), atmoMat);
    earthGroup.add(atmo);

    // ---------- LUA (bem visível, orbitando no plano da câmera) ----------
    const moonPivot = new THREE.Group(); scene.add(moonPivot);
    const moon = new THREE.Mesh(new THREE.SphereGeometry(EARTH_R * 0.32, 64, 64),
      new THREE.MeshPhongMaterial({ map: load('moon.jpg'), bumpMap: load('moon_bump.jpg', false), bumpScale: 0.01, shininess: 1 }));
    moon.position.set(14, 0, 0); moonPivot.add(moon); // longe (não entra na câmera)

    // ---------- SATÉLITES MINÚSCULOS DETALHADOS ----------
    const sats = [];
    const makeSat = () => {
      const g = new THREE.Group();
      const body = new THREE.Mesh(new THREE.CylinderGeometry(0.0025, 0.0025, 0.007, 8), new THREE.MeshStandardMaterial({ color: 0xd4af37, metalness: 1, roughness: 0.35 }));
      body.rotation.z = Math.PI/2; g.add(body);
      const panelMat = new THREE.MeshStandardMaterial({ color: 0x0a1f5c, emissive: 0x0a2a6a, emissiveIntensity: 0.7, metalness: 0.7, roughness: 0.3 });
      [-1,1].forEach(sgn => {
        const panel = new THREE.Mesh(new THREE.BoxGeometry(0.011,0.0008,0.006), panelMat); panel.position.x=sgn*0.011; g.add(panel);
      });
      return g;
    };
    for (let k=0;k<8;k++){
      const pivot = new THREE.Group();
      pivot.rotation.x=(Math.random()-0.5)*2.2; pivot.rotation.z=(Math.random()-0.5)*2.2; scene.add(pivot);
      const b = makeSat(); b.position.x = EARTH_R*1.06 + k*0.05; pivot.add(b);
      sats.push({ pivot, body:b, speed:0.06+Math.random()*0.05 });
    }

    // ---------- ESTRELAS (skybox) ----------
    // ESTRELAS 3D REAIS — milhares de pontos em profundidade (não é wallpaper)
    const STAR_N = 6000;
    const starPos = new Float32Array(STAR_N * 3);
    const starCol = new Float32Array(STAR_N * 3);
    const starSize = new Float32Array(STAR_N);
    for (let i = 0; i < STAR_N; i++) {
      // distribui numa casca esférica grossa (profundidade real)
      const r = 200 + Math.random() * 1800;
      const th = Math.random() * Math.PI * 2;
      const ph = Math.acos(2 * Math.random() - 1);
      starPos[i*3] = r * Math.sin(ph) * Math.cos(th);
      starPos[i*3+1] = r * Math.sin(ph) * Math.sin(th);
      starPos[i*3+2] = r * Math.cos(ph);
      // cor: maioria branca-azulada, algumas alaranjadas/avermelhadas (estrelas reais)
      const t = Math.random();
      let c;
      if (t < 0.7) c = new THREE.Color().setHSL(0.6, 0.15, 0.85 + Math.random()*0.15);
      else if (t < 0.9) c = new THREE.Color().setHSL(0.09, 0.6, 0.7);
      else c = new THREE.Color().setHSL(0.02, 0.7, 0.6);
      starCol[i*3] = c.r; starCol[i*3+1] = c.g; starCol[i*3+2] = c.b;
      starSize[i] = (Math.random() < 0.05 ? 3 + Math.random()*4 : 0.8 + Math.random()*1.8);
    }
    const starGeo = new THREE.BufferGeometry();
    starGeo.setAttribute('position', new THREE.BufferAttribute(starPos, 3));
    starGeo.setAttribute('color', new THREE.BufferAttribute(starCol, 3));
    starGeo.setAttribute('aSize', new THREE.BufferAttribute(starSize, 1));
    // textura de estrela (brilho radial)
    const sc = document.createElement('canvas'); sc.width = sc.height = 64;
    const sg = sc.getContext('2d'); const sgr = sg.createRadialGradient(32,32,0,32,32,32);
    sgr.addColorStop(0,'#fff'); sgr.addColorStop(0.3,'rgba(255,255,255,0.8)'); sgr.addColorStop(1,'rgba(0,0,0,0)');
    sg.fillStyle = sgr; sg.fillRect(0,0,64,64);
    const starTex = new THREE.CanvasTexture(sc);
    // shader para cintilação + tamanho por estrela
    const starMat = new THREE.ShaderMaterial({
      uniforms: { uTex: { value: starTex }, uTime: { value: 0 }, uPixelRatio: { value: Math.min(window.devicePixelRatio, 2) } },
      transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, vertexColors: true,
      vertexShader: `attribute float aSize; varying vec3 vColor; uniform float uTime; uniform float uPixelRatio;
        void main(){ vColor = color; vec4 mv = modelViewMatrix * vec4(position, 1.0);
          float tw = 0.7 + 0.3 * sin(uTime * 2.0 + position.x * 0.5 + position.y * 0.3);
          gl_PointSize = aSize * uPixelRatio * (300.0 / -mv.z) * tw;
          gl_Position = projectionMatrix * mv; }`,
      fragmentShader: `uniform sampler2D uTex; varying vec3 vColor;
        void main(){ vec4 t = texture2D(uTex, gl_PointCoord); if (t.a < 0.02) discard; gl_FragColor = vec4(vColor, 1.0) * t; }`,
    });
    const starField = new THREE.Points(starGeo, starMat);
    scene.add(starField);

    // ---------- ESTRELAS CINTILANTES (pontos que brilham/piscam) ----------
    const TW = 700;
    const twPos = new Float32Array(TW*3), twPhase = new Float32Array(TW), twBase = new Float32Array(TW);
    for (let i=0;i<TW;i++){ const rr=200+Math.random()*800; const th=Math.random()*6.283, phi=Math.acos(2*Math.random()-1);
      twPos[i*3]=rr*Math.sin(phi)*Math.cos(th); twPos[i*3+1]=rr*Math.sin(phi)*Math.sin(th); twPos[i*3+2]=rr*Math.cos(phi);
      twPhase[i]=Math.random()*6.283; twBase[i]=0.5+Math.random()*0.5; }
    const twGeo=new THREE.BufferGeometry(); twGeo.setAttribute('position',new THREE.BufferAttribute(twPos,3));
    const dc=document.createElement('canvas'); dc.width=dc.height=64; const dgx=dc.getContext('2d'); const gr=dgx.createRadialGradient(32,32,0,32,32,32); gr.addColorStop(0,'#fff'); gr.addColorStop(0.35,'rgba(255,255,255,0.5)'); gr.addColorStop(1,'rgba(0,0,0,0)'); dgx.fillStyle=gr; dgx.fillRect(0,0,64,64);
    const twMat=new THREE.PointsMaterial({size:3.5,map:new THREE.CanvasTexture(dc),transparent:true,opacity:0.9,blending:THREE.AdditiveBlending,depthWrite:false,sizeAttenuation:true,color:0xeaf2ff});
    const twinkle=new THREE.Points(twGeo,twMat); scene.add(twinkle);

    // ---------- LENS FLARE do Sol ----------
    const flareCanvas = document.createElement('canvas'); flareCanvas.width=flareCanvas.height=256;
    const fx=flareCanvas.getContext('2d'); const fg=fx.createRadialGradient(128,128,0,128,128,128);
    fg.addColorStop(0,'rgba(255,255,255,1)'); fg.addColorStop(0.3,'rgba(255,240,200,0.6)'); fg.addColorStop(1,'rgba(255,200,120,0)');
    fx.fillStyle=fg; fx.fillRect(0,0,256,256); const flareTex=new THREE.CanvasTexture(flareCanvas);
    const lensflare = new Lensflare();
    lensflare.addElement(new LensflareElement(flareTex, 350, 0, new THREE.Color(0xfff2d0)));
    lensflare.addElement(new LensflareElement(flareTex, 90, 0.35, new THREE.Color(0xffd090)));
    lensflare.addElement(new LensflareElement(flareTex, 55, 0.55, new THREE.Color(0xff9955)));
    lensflare.addElement(new LensflareElement(flareTex, 130, 0.75, new THREE.Color(0xffcc88)));
    lensflare.addElement(new LensflareElement(flareTex, 45, 0.95, new THREE.Color(0xffe0aa)));
    sunLight.add(lensflare);

    // ---------- METEOROS COM FÍSICA REAL (gravidade Terra+Lua, entram/queimam ou passam direto) ----------
    const meteors = [];
    // textura de rastro realista (núcleo branco quente -> laranja -> transparente)
    const trailTex = (() => { const c=document.createElement('canvas'); c.width=c.height=128; const g=c.getContext('2d');
      const gr=g.createRadialGradient(64,64,0,64,64,64);
      gr.addColorStop(0,'rgba(255,255,255,1)'); gr.addColorStop(0.15,'rgba(255,245,220,0.95)');
      gr.addColorStop(0.35,'rgba(255,190,110,0.6)'); gr.addColorStop(0.6,'rgba(255,120,50,0.25)'); gr.addColorStop(1,'rgba(255,80,30,0)');
      g.fillStyle=gr; g.fillRect(0,0,128,128); return new THREE.CanvasTexture(c); })();
    const spawnMeteor = () => {
      const sizeMet = 0.004 + Math.random()*0.012; // MINÚSCULO (tamanho de satélite)
      const len = 60;
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(len*3), 3));
      const geoCol = new Float32Array(len*3); geo.setAttribute('color', new THREE.BufferAttribute(geoCol, 3));
      const mat = new THREE.PointsMaterial({ size: sizeMet*8, map: trailTex, vertexColors: true, transparent: true, opacity: 1, blending: THREE.AdditiveBlending, depthWrite: false, sizeAttenuation: true });
      const pts = new THREE.Points(geo, mat); scene.add(pts);
      // vem de longe numa direção que passa perto da Terra (não direto no centro)
      const R0 = 40 + Math.random()*30;
      const th = Math.random()*6.283, ph = Math.acos(2*Math.random()-1);
      const pos = new THREE.Vector3(R0*Math.sin(ph)*Math.cos(th), R0*Math.sin(ph)*Math.sin(th), R0*Math.cos(ph));
      // alvo com offset (impact parameter) — decide se raspa, entra ou passa
      const aim = new THREE.Vector3((Math.random()-0.5)*10, (Math.random()-0.5)*10, (Math.random()-0.5)*10);
      const vel = aim.clone().sub(pos).normalize().multiplyScalar(6 + Math.random()*4);
      meteors.push({ geo, mesh: pts, mat, len, pos, vel, sizeMet, hist: [], burning: false, dead: false, life: 0 });
    };

    // impactos (quando um meteoro ENTRA na atmosfera): flash pequeno na superfície
    const impacts = [];
    const G_EARTH = 55;  // constante de gravidade da Terra (ajustada p/ visual)
    const G_MOON = 3;    // gravidade da Lua (bem menor)

    // ---------- BLOOM ----------
    const composer = new EffectComposer(renderer); composer.setSize(W(), H());
    composer.addPass(new RenderPass(scene, camera));
    const bloom = new UnrealBloomPass(new THREE.Vector2(W(), H()), 0.45, 0.8, 0.9);
    composer.addPass(bloom);
    const onResize = () => { camera.aspect=W()/H(); camera.updateProjectionMatrix(); renderer.setSize(W(),H()); composer.setSize(W(),H()); };
    window.addEventListener('resize', onResize);

    let raf, t=0, curZoom=7, nextMeteor=1.5;
    const clock = new THREE.Clock();
    const animate = () => {
      raf = requestAnimationFrame(animate);
      const dt = Math.min(clock.getDelta(), 0.05); t += dt;
      const ph = phaseRef.current;
      const speaking=ph==='speaking', listening=ph==='listening', thinking=ph==='thinking';
      const energy = speaking?1:listening?0.5:thinking?0.7:0.28;

      earth.rotation.y += dt*(0.02+energy*0.012);
      clouds.rotation.y += dt*(0.026+energy*0.012);
      moonPivot.rotation.y += dt*0.02;
      moon.rotation.y += dt*0.008;
      sats.forEach(s=>{ s.pivot.rotation.y += dt*s.speed; s.body.rotation.y += dt*0.2; });
      starField.rotation.y += dt*0.0006;
      if (starMat.uniforms) starMat.uniforms.uTime.value = t;
      sun.rotation.y += dt*0.005;

      // estrelas cintilando (opacidade por seno individual)
      twMat.opacity = 0.85; // base
      const twp = twGeo.attributes.position; // usamos size via material global + variação por brilho
      twMat.size = 3.0 + Math.sin(t*3)*0.6;

      earthMat.emissiveIntensity = 1.5+energy*0.3;
      bloom.strength = 0.4+energy*0.25;

      // posição atual da Lua no mundo
      const moonWorld = new THREE.Vector3(); moon.getWorldPosition(moonWorld);

      // spawn meteoros (físicos)
      nextMeteor -= dt;
      if (nextMeteor <= 0) { spawnMeteor(); nextMeteor = 3 + Math.random()*5; }
      for (let m = meteors.length-1; m>=0; m--) {
        const mt = meteors[m]; mt.life += dt;
        // GRAVIDADE: Terra (origem) + Lua puxam o meteoro (F = G/r^2 na direção do corpo)
        const toEarth = mt.pos.clone().negate(); const dE = toEarth.length();
        const aE = toEarth.normalize().multiplyScalar(G_EARTH / Math.max(dE*dE, 4));
        const toMoon = moonWorld.clone().sub(mt.pos); const dM = toMoon.length();
        const aM = toMoon.normalize().multiplyScalar(G_MOON / Math.max(dM*dM, 1));
        mt.vel.addScaledVector(aE, dt).addScaledVector(aM, dt);
        mt.pos.addScaledVector(mt.vel, dt);

        // entrou na atmosfera? (distância do centro < raio atmosférico)
        const distC = mt.pos.length();
        const atmR = EARTH_R * 1.06;
        if (!mt.burning && distC < atmR && distC > EARTH_R*0.9) {
          mt.burning = true; // começou a queimar
        }
        // colidiu com a superfície -> flash e morre
        if (distC <= EARTH_R * 1.005) {
          const n = mt.pos.clone().normalize();
          const flash = new THREE.Mesh(new THREE.SphereGeometry(0.008 + mt.sizeMet*0.4, 10, 10), new THREE.MeshBasicMaterial({ color: 0xfff0c0, transparent: true, opacity: 1, blending: THREE.AdditiveBlending, depthWrite: false }));
          flash.position.copy(n.multiplyScalar(EARTH_R*1.005));
          scene.add(flash);
          impacts.push({ flash, life: 0, max: 0.8 + mt.sizeMet*3 });
          mt.dead = true;
        }
        // trilha: rastro proporcional ao tamanho; ao queimar fica laranja quente e mais denso
        mt.hist.unshift(mt.pos.clone()); if (mt.hist.length>mt.len) mt.hist.pop();
        const arr = mt.geo.attributes.position.array; const col = mt.geo.attributes.color.array;
        for (let i=0;i<mt.len;i++){
          const p2=mt.hist[i]||mt.hist[mt.hist.length-1]||mt.pos;
          arr[i*3]=p2.x; arr[i*3+1]=p2.y; arr[i*3+2]=p2.z;
          const fade = 1 - i/mt.len;
          if (mt.burning) { col[i*3]=1; col[i*3+1]=0.5*fade+0.3; col[i*3+2]=0.1*fade; } // laranja fogo
          else { col[i*3]=0.8*fade+0.2; col[i*3+1]=0.85*fade+0.15; col[i*3+2]=fade+0.0; } // branco-azulado frio
        }
        mt.geo.attributes.position.needsUpdate = true; mt.geo.attributes.color.needsUpdate = true;
        mt.mat.size = (mt.burning ? mt.sizeMet*14 : mt.sizeMet*8);
        mt.mat.opacity = mt.burning ? 1 : 0.85;

        // morre se: colidiu, ou saiu muito longe (passou direto), ou queimou completamente na atmosfera
        const tooFar = distC > 110;
        if (mt.burning && distC < atmR && mt.life > 0.6 && Math.random() < dt*1.5) mt.dead = true; // meteoro pequeno some queimando
        if (mt.dead || tooFar) { scene.remove(mt.mesh); mt.geo.dispose(); mt.mat.dispose(); meteors.splice(m,1); }
      }

      // impactos (flash na superfície)
      for (let k=impacts.length-1;k>=0;k--){
        const im = impacts[k]; im.life += dt; const p2 = im.life/im.max;
        im.flash.material.opacity = Math.max(0, 1-p2);
        im.flash.scale.setScalar(1 + p2*3);
        if (im.life > im.max) { scene.remove(im.flash); im.flash.geometry.dispose(); im.flash.material.dispose(); impacts.splice(k,1); }
      }

      // câmera BEM lenta e majestosa
      const target = speaking?6.4:listening?6.9:7; curZoom += (target-curZoom)*0.015;
      const orbit = t*0.018;
      camera.position.x = Math.sin(orbit)*curZoom;
      camera.position.z = Math.cos(orbit)*curZoom;
      camera.position.y = 0.7 + Math.sin(t*0.04)*0.6;
      camera.lookAt(0,0,0);
      composer.render();
    };
    animate();

    return () => {
      cancelAnimationFrame(raf); window.removeEventListener('resize', onResize);
      composer.dispose?.(); renderer.dispose();
      scene.traverse(o=>{ if(o.geometry)o.geometry.dispose(); if(o.material){const m=o.material;(Array.isArray(m)?m:[m]).forEach(x=>{Object.values(x).forEach(v=>v&&v.isTexture&&v.dispose()); x.dispose&&x.dispose();});} });
      try { mount.removeChild(renderer.domElement); } catch {}
    };
  }, []);

  return <div ref={mountRef} className="fixed inset-0 w-full h-full pointer-events-none" style={{ zIndex: 0 }} />;
}
